import { apiClient } from './api';

export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  phone: string;
  dateOfBirth?: string | null;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    pincode?: string;
    country?: string;
  } | null;
  preferences?: {
    skinType?: string;
    hairType?: string;
    allergies?: string[];
    notes?: string;
  } | null;
  loyaltyPoints: number;
  totalSpent: number;
  lastVisit?: string | null;
  visitCount: number;
  status: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
  createdAt: string;
}

export interface CreateCustomerData {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  dateOfBirth?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    pincode?: string;
    country?: string;
  };
  preferences?: {
    skinType?: string;
    hairType?: string;
    allergies?: string[];
    notes?: string;
  };
}

export interface UpdateCustomerData extends Partial<CreateCustomerData> {
  status?: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
}

export interface CustomersResponse {
  customers: Customer[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CustomerFilters {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ImportCustomersResult {
  created: number;
  skippedExisting: number;
  skippedDuplicates: number;
  skippedInvalid: number;
  totalParsed: number;
  skippedRecords: Array<{
    name?: string;
    phone?: string;
    reason: string;
  }>;
  invalidRecords: Array<{
    name?: string;
    phone?: string;
    reason: string;
  }>;
}

class CustomerService {
  private baseUrl = '/customers';

  async getCustomers(filters: CustomerFilters = {}): Promise<CustomersResponse> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await apiClient.get<{ data: { customers: Customer[] }; pagination?: any }>(
      `${this.baseUrl}?${params.toString()}`
    );

    return {
      customers: response?.data?.customers ?? [],
      pagination: response?.pagination,
    };
  }

  async getCustomerById(id: string): Promise<Customer | null> {
    const response = await apiClient.get<{ data: { customer: Customer } }>(`${this.baseUrl}/${id}`);
    return (response as any)?.data?.customer ?? null;
  }

  async createCustomer(data: CreateCustomerData): Promise<Customer> {
    const response = await apiClient.post<{ data: { customer: Customer } }>(this.baseUrl, data);
    return (response as any)?.data?.customer ?? (response as any)?.data ?? response;
  }

  async updateCustomer(id: string, data: UpdateCustomerData): Promise<Customer> {
    const response = await apiClient.put<{ data: { customer: Customer } }>(`${this.baseUrl}/${id}`, data);
    return (response as any)?.data?.customer ?? (response as any)?.data ?? response;
  }

  async deleteCustomer(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async importVCardContacts(vcardText: string): Promise<ImportCustomersResult> {
    const response = await apiClient.post<{ data: { import: ImportCustomersResult } }>(
      `${this.baseUrl}/import-vcard`,
      { vcardText }
    );
    return (response as any)?.data?.import;
  }

  async importCsvContacts(csvText: string): Promise<ImportCustomersResult> {
    const response = await apiClient.post<{ data: { import: ImportCustomersResult } }>(
      `${this.baseUrl}/import-csv`,
      { csvText }
    );
    return (response as any)?.data?.import;
  }

  /**
   * Downloads a CSV or VCF of customers honoring the current search/status
   * filters. Uses fetch directly so we can stream the response as a Blob.
   */
  async exportCustomers(
    format: 'vcf' | 'csv',
    filters: { search?: string; status?: string } = {}
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.set('format', format);
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);

    const token = localStorage.getItem('token') ?? '';
    const tenantId = localStorage.getItem('salonId') ?? localStorage.getItem('tenantId') ?? '';
    const baseUrl = (import.meta as any).env?.VITE_API_URL ?? '/api/v1';

    const res = await fetch(`${baseUrl}${this.baseUrl}/export?${params.toString()}`, {
      headers: {
        Accept: format === 'vcf' ? 'text/vcard' : 'text/csv',
        Authorization: token ? `Bearer ${token}` : '',
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      },
    });
    if (!res.ok) {
      let msg = `Export failed (${res.status})`;
      try {
        const body = await res.json();
        if (body?.error?.message) msg = body.error.message;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    return await res.blob();
  }

  async getAnalytics(): Promise<CustomerAnalytics | null> {
    try {
      const response = await apiClient.get<{ data: { analytics: CustomerAnalytics } }>(
        `${this.baseUrl}/analytics`
      );
      return (response as any)?.data?.analytics ?? null;
    } catch {
      return null;
    }
  }
}

export interface CustomerAnalytics {
  totalCustomers: number;
  activeCustomers: number;
  newCustomersThisMonth: number;
  topCustomers: Array<{
    id: string;
    firstName: string;
    lastName: string;
    totalSpent: number;
    visitCount: number;
    loyaltyPoints: number;
  }>;
  customersByStatus: Record<string, number>;
}

export const customerService = new CustomerService();
